import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { Skeleton } from './Skeleton'

const tones = {
  brand: 'from-brand-500 to-brand-600 shadow-brand-500/30',
  violet: 'from-accent-500 to-accent-600 shadow-accent-500/30',
  success: 'from-success-500 to-success-600 shadow-success-500/30',
  warning: 'from-warning-500 to-warning-600 shadow-warning-500/30',
  info: 'from-info-500 to-info-600 shadow-info-500/30',
  danger: 'from-danger-500 to-danger-600 shadow-danger-500/30',
} as const

interface StatCardProps {
  label: string
  value: ReactNode
  icon: ReactNode
  tone?: keyof typeof tones
  hint?: ReactNode
  loading?: boolean
  footer?: ReactNode
}

export function StatCard({ label, value, icon, tone = 'brand', hint, loading, footer }: StatCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-card border border-line bg-surface p-5 shadow-card transition-shadow hover:shadow-raised">
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -top-10 -right-10 size-32 rounded-full bg-gradient-to-br opacity-[0.07] transition-opacity group-hover:opacity-[0.12]',
          tones[tone],
        )}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium tracking-wide text-muted uppercase">{label}</p>
          {loading ? (
            <Skeleton className="mt-2 h-8 w-20" />
          ) : (
            <p className="mt-1.5 text-[1.75rem] leading-none font-semibold tracking-tight text-ink tabular">
              {value}
            </p>
          )}
        </div>
        <span
          className={cn(
            'grid size-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-white shadow-lg [&_svg]:size-5',
            tones[tone],
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      </div>
      {hint ? (
        <div className="mt-3 text-xs text-muted">{loading ? <Skeleton className="h-3 w-32" /> : hint}</div>
      ) : null}
      {footer}
    </div>
  )
}
